import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, Navigate, createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { AlertCircle, RefreshCw, SearchX, UserPlus } from "lucide-react";

import { funnelStage } from "@/api/triage/state-schema";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Pager } from "@/components/ui/pager";
import { optional, pageParam, qParam, sourceParam } from "@/lib/search-params";

import { PessoasCards } from "./-components/pessoas-cards";
import { PessoasControls } from "./-components/pessoas-controls";
import { PessoasImportReview } from "./-components/pessoas-import-review";

const linkParam = optional(
	type.enumerated("only_conversation", "patient_potential", "patient_confirmed"),
);

const stageParam = optional(funnelStage.or(type.enumerated("none")));

const searchSchema = type({
	"q?": qParam,
	"link?": linkParam,
	"source?": sourceParam,
	"stage?": stageParam,
	include_non_human: "boolean = false",
	problems: "boolean = false",
	page: pageParam,
});

export type PessoasSearch = typeof searchSchema.infer;

export const Route = createFileRoute("/_app/pessoas/")({
	validateSearch: searchSchema,
	component: PessoasDirectory,
});

function PessoasDirectory() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();

	const { data, isLoading, isError, isFetching, isPlaceholderData, refetch } = useQuery(
		orpc.people.directory.queryOptions({
			input: search,
			placeholderData: keepPreviousData,
		}),
	);

	if (!isError && !isPlaceholderData && data !== undefined && search.page > data.pages) {
		return <Navigate to="/pessoas" search={(prev) => ({ ...prev, page: data.pages })} replace />;
	}

	return (
		<PageContainer>
			<PageHeader
				eyebrow="Diretório"
				title="Pessoas"
				actions={
					<Button asChild>
						<Link to="/cadastro" data-slot="novo-cadastro">
							<UserPlus className="size-4" />
							Novo cadastro
						</Link>
					</Button>
				}
			/>

			<section aria-label="Diretório de pessoas" className="workspace-frame">
				<PessoasControls
					counts={isError ? undefined : data?.counts}
					total={isError ? undefined : data?.total}
					isFetching={isFetching}
				/>

				{search.problems && <PessoasImportReview />}

				{isError && (
					<div role="alert" className="surface-panel px-4 pb-6 text-center shadow-control">
						<EmptyState icon={AlertCircle} title="Não foi possível carregar o diretório." />
						<Button type="button" variant="outline" onClick={() => refetch()} disabled={isFetching}>
							<RefreshCw aria-hidden="true" className={isFetching ? "animate-spin" : undefined} />
							Tentar novamente
						</Button>
					</div>
				)}

				{!isError && (isLoading || (isPlaceholderData && data.items.length === 0)) && (
					<SkeletonRows rows={10} />
				)}

				{!isError && data !== undefined && (
					<div aria-busy={isFetching} className="flex flex-col gap-4">
						{data.items.length === 0 && !isPlaceholderData && (
							<div className="surface-panel px-4 shadow-control">
								<EmptyState icon={SearchX} title="Nenhuma pessoa encontrada" />
							</div>
						)}
						{data.items.length > 0 && <PessoasCards items={data.items} search={search} />}
						{data.pages > 1 && (
							<div
								className={isPlaceholderData ? "pointer-events-none opacity-50" : undefined}
								inert={isPlaceholderData}
							>
								<Pager
									page={search.page}
									pages={data.pages}
									onPage={(next) => navigate({ search: (prev) => ({ ...prev, page: next }) })}
								/>
							</div>
						)}
					</div>
				)}
			</section>
		</PageContainer>
	);
}
